import type { FeatureModule, FeatureState } from '../types';

/**
 * Lesefreundliche Schrift (dyslexia-friendly typography).
 *
 * BEWUSST KEINE externe Schrift (OpenDyslexic o. Ä.): der externe Font-Inject
 * ist auf vielen Kundenseiten per CSP (`style-src`/`font-src 'self'`) geblockt
 * — genau deshalb ist `injectBrandFonts()` in index.ts ein No-op. Stattdessen
 * ein gut lesbarer, system-verfügbarer Sans-Stack PLUS die nachweislich
 * stärker wirkenden Hebel bei Legasthenie: größere Zeilenhöhe, Buchstaben- und
 * Wortabstand. Robust, ohne Netzwerk-/CSP-Risiko.
 *
 * Icon-Fonts (FontAwesome `<i class="fa…">`, Material Icons via Ligaturen,
 * Glyphicons) werden ausgenommen — sonst zerstören Font-Swap + Letter-Spacing
 * deren Glyphen/Ligaturen. Das Widget selbst (#accessify-root) nie anfassen.
 */
export default function createDyslexiaFontModule(): FeatureModule {
  let enabled = false;
  const STYLE_ID = 'accessify-dyslexia-font';
  const STORAGE_KEY = 'accessify-dyslexia-font';

  // Widget-Shadow-Host + Icon-Font-Träger ausnehmen.
  const EX =
    ':not(#accessify-root):not(#accessify-root *)' +
    ':not(i[class]):not([class*="icon"]):not([class*="fa-"])' +
    ':not([class*="material-icons"]):not([class*="material-symbols"]):not(.glyphicon)';

  // Atkinson Hyperlegible (falls vom Host geladen) → sonst breite, gut
  // unterscheidbare System-Sans. Verdana/Tahoma sind auf praktisch jedem OS da.
  const READABLE_STACK =
    '"Atkinson Hyperlegible", Verdana, Tahoma, "Trebuchet MS", "Segoe UI", system-ui, sans-serif';

  function getStyles(): string {
    return `
      body :where(p,h1,h2,h3,h4,h5,h6,a,span,li,td,th,dd,dt,blockquote,label,button,input,textarea,select,figcaption,caption,summary,strong,em,small,div)${EX} {
        font-family: ${READABLE_STACK} !important;
        /* Wenn Text-Abstände (spacing) aktiv sind, gewinnen deren größere
         * WCAG-1.4.12-Werte über die --accessify-*-Variablen; ohne spacing
         * greifen die eigenen, moderateren Fallbacks. Vorher überschrieb
         * diese Regel (ID-Spezifität + !important) die spacing-Werte und
         * brach still das WCAG-Versprechen der Kombination. */
        letter-spacing: var(--accessify-letter-spacing, 0.04em) !important;
        word-spacing: var(--accessify-word-spacing, 0.12em) !important;
        line-height: var(--accessify-line-height, 1.7) !important;
      }
    `;
  }

  function injectStyles() {
    let styleEl = document.getElementById(STYLE_ID);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = getStyles();
  }

  function removeStyles() {
    document.getElementById(STYLE_ID)?.remove();
  }

  function activate() {
    enabled = true;
    injectStyles();
    localStorage.setItem(STORAGE_KEY, 'true');
  }

  function deactivate() {
    enabled = false;
    removeStyles();
    localStorage.removeItem(STORAGE_KEY);
  }

  return {
    id: 'dyslexia-font',
    name: () => 'Readable font',
    description: 'Switch to a legible font with more spacing',
    icon: 'dyslexia-font',
    category: 'visual',
    activate,
    deactivate,
    getState: (): FeatureState => ({ id: 'dyslexia-font', enabled }),
    setState: (state: { enabled: boolean }) => {
      if (state.enabled) activate();
      else deactivate();
    },
  };
}
